import React, { useState } from 'react';
import {
    Box,
    SimpleGrid,
    Icon,
    Flex,
    Skeleton,
    Button,
    Input,
    Text,
    HStack,
    VStack,
    useToast,
} from '@chakra-ui/react';
import { FaDollarSign, FaShoppingCart, FaTasks, FaCheckCircle, FaTimesCircle, FaChartLine, FaDownload, FaCalendarAlt } from 'react-icons/fa';
import { exportarMetricasVendedor } from '../../services/api';

const StatCard = ({ icon, label, value, currency = false, accent = '#F68633', bgIcon, loading }) => (
    <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        borderWidth="1px"
        borderColor="gray.200"
        borderTopWidth="3px"
        borderTopColor={accent}
        p={{ base: 4, md: 5 }}
        transition="all 0.2s"
        _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
    >
        <Flex align="center" gap={4}>
            <Flex
                bg={bgIcon || `${accent}15`}
                w={12}
                h={12}
                borderRadius="lg"
                align="center"
                justify="center"
                flexShrink={0}
            >
                <Icon as={icon} color={accent} boxSize={5} />
            </Flex>
            <VStack align="flex-start" spacing={0.5} flex="1" minW={0}>
                <Text fontSize="xs" color="gray.500" fontWeight="semibold" textTransform="uppercase" letterSpacing="wider" noOfLines={1}>
                    {label}
                </Text>
                {loading ? (
                    <Skeleton height="28px" width="100px" mt={1} />
                ) : (
                    <Text fontSize={{ base: 'xl', md: '2xl' }} fontWeight="bold" color="gray.800" lineHeight="short">
                        {currency && <Text as="span" fontSize="md" color="gray.500" mr={1}>R$</Text>}{value}
                    </Text>
                )}
            </VStack>
        </Flex>
    </Box>
);

const MetricasVendasVendedor = ({ metricas, loading, userId }) => {
    const toast = useToast();
    const [isExporting, setIsExporting] = useState(false);

    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];

    const [dataInicio, setDataInicio] = useState(firstDayOfMonth);
    const [dataFim, setDataFim] = useState(lastDayOfMonth);

    const handleExport = async () => {
        if (!userId) {
            toast({ title: 'Vendedor não selecionado.', status: 'error', isClosable: true });
            return;
        }

        if (!dataInicio || !dataFim) {
            toast({ title: 'Por favor, selecione um período de datas.', status: 'warning', isClosable: true });
            return;
        }

        setIsExporting(true);
        try {
            const response = await exportarMetricasVendedor(userId, dataInicio, dataFim);
            if (response && response.data) {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const contentDisposition = response.headers['content-disposition'];
                let fileName = 'relatorio-vendas.xlsx';
                if (contentDisposition) {
                    const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
                    if (fileNameMatch.length === 2)
                        fileName = fileNameMatch[1];
                }
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast({ title: 'Relatório gerado com sucesso!', status: 'success', isClosable: true });
            } else {
                throw new Error('Falha ao gerar o relatório.');
            }
        } catch (error) {
            toast({ title: 'Erro ao gerar relatório.', description: 'Tente novamente mais tarde.', status: 'error', isClosable: true });
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box maxW={{ base: '100%', md: '92%', lg: '85%' }} mx="auto" px={{ base: 3, md: 6 }}>
            <Flex
                bg="white"
                borderRadius="xl"
                borderWidth="1px"
                borderColor="gray.200"
                boxShadow="sm"
                p={{ base: 3, md: 4 }}
                mb={4}
                gap={3}
                align={{ base: 'stretch', md: 'center' }}
                direction={{ base: 'column', md: 'row' }}
                flexWrap="wrap"
            >
                <HStack spacing={2} flex="1">
                    <Icon as={FaCalendarAlt} color="gray.500" />
                    <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        Exportar relatório do período
                    </Text>
                </HStack>
                <HStack spacing={2} flexWrap="wrap">
                    <Input
                        type="date"
                        size="sm"
                        borderRadius="md"
                        value={dataInicio}
                        onChange={(e) => setDataInicio(e.target.value)}
                        maxW="160px"
                    />
                    <Text fontSize="xs" color="gray.500">até</Text>
                    <Input
                        type="date"
                        size="sm"
                        borderRadius="md"
                        value={dataFim}
                        onChange={(e) => setDataFim(e.target.value)}
                        maxW="160px"
                    />
                    <Button
                        size="sm"
                        leftIcon={<FaDownload />}
                        bg="#F68633"
                        color="white"
                        _hover={{ bg: '#E07520' }}
                        onClick={handleExport}
                        isLoading={isExporting}
                        loadingText="Gerando..."
                    >
                        Baixar
                    </Button>
                </HStack>
            </Flex>

            <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3} px={1}>
                Performance financeira do mês
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={5}>
                <StatCard
                    icon={FaDollarSign}
                    label="Total Vendido Bruto"
                    value={metricas?.totalVendidoBruto || '0,00'}
                    currency
                    accent="#38A169"
                    loading={loading}
                />
                <StatCard
                    icon={FaShoppingCart}
                    label="Total de Pedidos"
                    value={metricas?.totalPedidosMes || '0'}
                    accent="#3182CE"
                    loading={loading}
                />
                <StatCard
                    icon={FaChartLine}
                    label="Ticket Médio"
                    value={metricas?.ticketMedio || '0,00'}
                    currency
                    accent="#805AD5"
                    loading={loading}
                />
            </SimpleGrid>

            <Text fontSize="xs" fontWeight="bold" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb={3} px={1}>
                Status das vendas
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <StatCard
                    icon={FaTasks}
                    label="Em Processamento"
                    value={metricas?.vendasProcessamento || '0'}
                    accent="#F68633"
                    loading={loading}
                />
                <StatCard
                    icon={FaCheckCircle}
                    label="Concluídas"
                    value={metricas?.vendasConcluidas || '0'}
                    accent="#38A169"
                    loading={loading}
                />
                <StatCard
                    icon={FaTimesCircle}
                    label="Canceladas"
                    value={metricas?.vendasCanceladas || '0'}
                    accent="#E53E3E"
                    loading={loading}
                />
            </SimpleGrid>
        </Box>
    );
};

export default MetricasVendasVendedor;
